import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import Checkbox from 'components/Checkbox';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { postAppointmentForm, setFormData } from 'modules/forms/actions/form.actions';
import { acuteOtitisMediaRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { getFormResultHTML } from 'modules/forms/helpers/formResult.helper';
import FormResult from './FormResult';

const ThirdAssessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      secondarySymptoms: stateForm.secondarySymptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, setValue, handleSubmit } = methods;
  const secondarySymptoms = watch('secondarySymptoms') || [];

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(setFormData(values));
    const status = stateForm.status || EAppointmentFormStatus.COMPLETED;
    dispatch(
      postAppointmentForm({
        form: {
          appointmentUuid: appointment?.uuid,
          status: values.secondarySymptoms?.includes('3')
            ? EAppointmentFormStatus.NEEDS_REVIEW
            : status,
          response: getFormResultHTML({ ...stateForm, ...values }, FormResult),
        },
        route: acuteOtitisMediaRoutes.confirmation,
      }),
    );
  };

  const onChangeValue = (name: string, e: ChangeEvent<HTMLInputElement>) => {
    setValue(
      'secondarySymptoms',
      e.target.checked
        ? [...secondarySymptoms, name].filter(item => item !== '3')
        : secondarySymptoms.filter(item => item !== name),
    );
  };

  return (
    <Page>
      <PageHeader step={5} maxSteps={5} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock title="Is the patient experiencing any of the following additional symptoms?">
                <Controller
                  name="secondarySymptoms"
                  control={control}
                  render={() => (
                    <FormGroup>
                      <Checkbox
                        id="1"
                        name="1"
                        checked={secondarySymptoms?.includes('1')}
                        onChange={e => onChangeValue('1', e)}
                        label="Any discharge or pus coming from the ear"
                      />
                      <Checkbox
                        id="2"
                        name="2"
                        checked={secondarySymptoms?.includes('2')}
                        onChange={e => onChangeValue('2', e)}
                        label="High fever"
                      />
                      <Checkbox
                        id="3"
                        name="3"
                        checked={secondarySymptoms?.includes('3')}
                        onChange={e => setValue('secondarySymptoms', e.target.checked ? ['3'] : [])}
                        label="None of the Above"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={!secondarySymptoms?.length}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default ThirdAssessment;
